import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ChartData, ChartDataset, TooltipItem } from 'chart.js';
import moment from 'moment';
import { ChartModule } from 'primeng/chart';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';

import { DashboardColorOptions, UserStatisticOptions } from '@core/constants/dashboard';
import { COLOR } from '@core/enum/color';
import { LeadsByUsersSortFieldEnum, USER_STATISTIC } from '@core/enum/dashboard';
import { ChartOption, LeadsByUsers } from '@core/models/interfaces/dashboard';
import { DashboardService } from '@core/services/dashboard.service';
import { DoughnutChartLegendComponent } from '@feature/dashboard/components/doughnut-chart-legend/doughnut-chart-legend.component';
import { CustomPaginatorComponent } from '@shared/components/custom-paginator/custom-paginator.component';
import { EmptyTableComponent } from '@shared/components/empty-table/empty-table.component';
import { SortTableHeaderComponent } from '@shared/components/sort-table-header/sort-table-header.component';
import { GENDER } from '@core/models/interfaces/common';
import { DashboardStatisticComponent } from '@shared/components/dashboard-statistic/dashboard-statistic.component';

@Component({
  selector: 'app-user-dashboard',
  standalone: true,
  imports: [
    ChartModule,
    TranslateModule,
    PaginatorModule,
    TableModule,
    CommonModule,
    DoughnutChartLegendComponent,
    DashboardStatisticComponent,
    CustomPaginatorComponent,
    EmptyTableComponent,
    SortTableHeaderComponent
  ],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss'
})
export class UserDashboardComponent implements OnChanges {
  @Input() rangeDates: Date[] = [];

  readonly unsubscribe$ = new Subject();
  readonly translatePrefix = 'dashboard.';
  readonly LeadsByUsersSortFieldEnum = LeadsByUsersSortFieldEnum;
  readonly doughnutChartOptions = {
    cutout: '70%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<'doughnut'>) {
            const total = tooltipItem?.dataset?.data?.reduce((a: number, b: number) => a + b, 0) || 0;
            return `${tooltipItem?.formattedValue} (${(((tooltipItem?.parsed || 0) / total) * 100).toFixed(2)}%)`;
          }
        }
      }
    }
  };
  readonly departmentChartOptions = {
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        }
      },
      y: {
        stacked: true,
        ticks: {
          maxTicksLimit: 7
        }
      }
    }
  };
  readonly usersByPositionChartOptions = {
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        ticks: {
          maxTicksLimit: 7
        }
      }
    }
  };

  userStatistics = UserStatisticOptions;
  usersByGender: ChartOption[] = [];
  usersByLocation: ChartOption[] = [];
  leadsByUsers: LeadsByUsers[] = [];

  sortField: string = LeadsByUsersSortFieldEnum.LEAD_TAKE_CARE;
  sortOrder: number = 0;
  isLoading: boolean = false;
  totalLeadsByUsers: number = 0;
  leadsByUsersPagination = {
    page: 0,
    size: 10
  };

  usersByGenderChartData: ChartData;
  usersByLocationChartData: ChartData;
  userByDepartmentChartData: ChartData;
  usersByPositionChartData: ChartData;

  constructor(private dashboardService: DashboardService) {}

  ngOnChanges() {
    this.fetchStatisticData();
    this.fetchChartData();
    this.fetchLeadsByUsers();
  }

  get rangeDatesPrams() {
    const dateFrom = this.rangeDates?.[0]
      ? moment(this.rangeDates?.[0]).startOf('date').toDate().toISOString()
      : undefined;
    const dateTo = this.rangeDates?.[1] ? moment(this.rangeDates?.[1]).endOf('date').toDate().toISOString() : undefined;

    return { dateFrom, dateTo };
  }

  get computedIndex() {
    return (index: number) => {
      return index + (this.leadsByUsersPagination?.page ?? 0) * (this.leadsByUsersPagination?.size ?? 10);
    };
  }

  fetchStatisticData() {
    const { dateFrom, dateTo } = this.rangeDatesPrams;

    this.dashboardService
      .getUserStatisticalData({
        from: dateFrom,
        to: dateTo
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          const data = res.data;
          for (let option of this.userStatistics) {
            switch (option.id) {
              case USER_STATISTIC.TOTAL_USERS: {
                option.rate = data.total?.rate;
                option.amount = data.total?.amount;
                break;
              }
              case USER_STATISTIC.ACTIVE_USERS: {
                option.rate = data.active?.rate;
                option.amount = data.active?.amount;
                break;
              }
              case USER_STATISTIC.HIDDEN_USERS: {
                option.rate = data.hidden?.rate;
                option.amount = data.hidden?.amount;
                break;
              }
              case USER_STATISTIC.INACTIVE_USERS: {
                option.rate = data.inactive?.rate;
                option.amount = data.inactive?.amount;
              }
            }
          }
        }
      });
  }

  fetchChartData() {
    const { dateFrom, dateTo } = this.rangeDatesPrams;

    this.dashboardService
      .getUserChartData({
        from: dateFrom,
        to: dateTo
      })
      .subscribe({
        next: res => {
          const genderChartData = res.data.gender;
          const departmentChartData = res.data.department;
          const positionChartData = res.data.position;
          const locationChartData = res.data.location;

          if (genderChartData) {
            this.usersByGender = genderChartData
              ?.sort((a, b) => b.amount - a.amount)
              ?.map(item => {
                return {
                  label: item?.option?.name,
                  data: item.amount,
                  color: this.getColorGender(item.option.code)
                };
              });

            this.usersByGenderChartData = {
              labels: this.usersByGender.map(item => item.label),
              datasets: [
                {
                  data: this.usersByGender.map(item => item.data),
                  backgroundColor: this.usersByGender.map(item => item.color)
                }
              ]
            };
          }

          if (departmentChartData) {
            const datasets: ChartDataset[] = [
              {
                label: 'Male',
                data: [],
                backgroundColor: this.getColorGender(GENDER.MALE),
                maxBarThickness: 40,
                borderRadius: 2
              },
              {
                label: 'Female',
                data: [],
                backgroundColor: this.getColorGender(GENDER.FEMALE),
                maxBarThickness: 40,
                borderRadius: 2
              },
              {
                label: 'Other',
                data: [],
                backgroundColor: this.getColorGender(GENDER.OTHER),
                maxBarThickness: 40,
                borderRadius: 2
              },
              {
                label: 'Unknown',
                data: [],
                backgroundColor: this.getColorGender(GENDER.UNKNOWN),
                maxBarThickness: 40,
                borderRadius: 2
              }
            ];

            const labels = departmentChartData.map(item => item.name);
            const genders = departmentChartData.map(item => item.options);

            datasets[0].data = genders.flatMap(gender => {
              return gender.filter(item => item.option.code === GENDER.MALE).map(item => item.amount);
            });
            datasets[1].data = genders.flatMap(gender => {
              return gender.filter(item => item.option.code === GENDER.FEMALE).map(item => item.amount);
            });
            datasets[2].data = genders.flatMap(gender => {
              return gender.filter(item => item.option.code === GENDER.OTHER).map(item => item.amount);
            });
            datasets[3].data = genders.flatMap(gender => {
              return gender.filter(item => item.option.code === GENDER.UNKNOWN).map(item => item.amount);
            });
            this.userByDepartmentChartData = {
              labels,
              datasets
            };
          }

          if (positionChartData) {
            const labels = positionChartData.map(item => item.option.name);
            const data = positionChartData.map(item => item.amount);
            this.usersByPositionChartData = {
              labels,
              datasets: [
                {
                  type: 'bar',
                  backgroundColor: COLOR.BLUE,
                  data,
                  maxBarThickness: 32,
                  borderRadius: 2
                }
              ]
            };
          }

          if (locationChartData) {
            this.usersByLocation = locationChartData
              ?.sort((a, b) => b.amount - a.amount)
              ?.map((item, index) => {
                return {
                  label: item?.option?.name,
                  data: item.amount,
                  color: DashboardColorOptions[index % 10].normal
                };
              });

            this.usersByLocationChartData = {
              labels: this.usersByLocation.map(item => item.label),
              datasets: [
                {
                  data: this.usersByLocation.map(item => item.data),
                  backgroundColor: this.usersByLocation.map(item => item.color)
                }
              ]
            };
          }
        }
      });
  }

  getColorGender(gender: string) {
    switch (gender) {
      case GENDER.MALE: {
        return COLOR.BLUE;
      }
      case GENDER.FEMALE: {
        return COLOR.PINK;
      }
      case GENDER.OTHER: {
        return COLOR.PURPLE;
      }
      default: {
        return COLOR.GRAY;
      }
    }
  }

  get isDataTable() {
    return this.leadsByUsers.length > 0;
  }

  fetchLeadsByUsers() {
    this.isLoading = true;
    const { dateFrom, dateTo } = this.rangeDatesPrams;
    const { page, size } = this.leadsByUsersPagination;

    const sorts = this.sortOrder > 0 ? 'DESC' : 'ASC';

    this.dashboardService
      .getLeadsByUsers({ from: dateFrom, to: dateTo, page, size, sorts: sorts })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          this.isLoading = false;
          this.leadsByUsers = res.data.content;
          this.totalLeadsByUsers = res.data.total;
        }
      });
  }

  onSortLeadsByUsers(event: { field: string; order: number }) {
    if (this.isLoading) return;
    this.sortField = event.field;
    this.sortOrder = event.order;
  }
}
